/** Wire adapters and adoption checks; allocation remains exclusively server-owned. */
import { create } from '@bufbuild/protobuf'
import {
  type AggregateEditIdentity,
  type AggregateEditUnit,
  AggregateEditUnitSchema,
  type PreviewAggregateEditResponse,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import { parseAggregateInteger } from './aggregateEdit'
import { editUnitKey } from './editUnits'
import { numberToPlainString } from './slotHelpers'
import type {
  DraftSaveAcknowledgement,
  RetailerEditRow,
  RetailerFieldsSnapshot,
} from './types'

export function aggregateUnit(row: RetailerEditRow): AggregateEditUnit {
  return create(AggregateEditUnitSchema, {
    kind:
      row.unit.kind === 'retailer'
        ? { case: 'retailer', value: {} }
        : row.unit.leaf.kind === 'group'
          ? {
              case: 'group',
              value: { demandGroupId: row.unit.leaf.demandGroupId },
            }
          : { case: 'residual', value: {} },
  })
}

export function aggregateIdentityKey(
  identity: AggregateEditIdentity | undefined,
): string | undefined {
  if (identity === undefined) return undefined
  const kind = identity.unit?.kind
  if (kind?.case === 'retailer') return identity.retailerBusinessCd
  if (kind?.case === 'group')
    return editUnitKey(identity.retailerBusinessCd, {
      kind: 'leaf',
      leaf: { kind: 'group', demandGroupId: kind.value.demandGroupId },
    })
  if (kind?.case === 'residual')
    return editUnitKey(identity.retailerBusinessCd, {
      kind: 'leaf',
      leaf: { kind: 'residual' },
    })
  return undefined
}

function baselineKey(baseline: RetailerFieldsSnapshot): string {
  return JSON.stringify([
    baseline.forecastSlots,
    baseline.providerSlots,
    [...baseline.manualEditKomas].sort((a, b) => a - b),
    baseline.provider,
    baseline.providerOther,
    baseline.providerLabel,
  ])
}

function matchesSavedMetadata(
  row: RetailerEditRow,
  baseline: RetailerFieldsSnapshot,
  version: RetailerEditRow['draftUpdatedAt'],
  carried: boolean,
): boolean {
  return (
    row.draftUpdatedAt?.seconds === version?.seconds &&
    row.draftUpdatedAt?.nanos === version?.nanos &&
    baselineKey(row.baseline) === baselineKey(baseline) &&
    row.carriedOverFromGroups === carried
  )
}

export function ownSaveAcknowledgementsReflected(
  rows: Readonly<Record<string, RetailerEditRow>>,
  acknowledgements: readonly DraftSaveAcknowledgement[],
  view: 'actor' | 'server',
): boolean {
  const latest = new Map(acknowledgements.map((ack) => [ack.key, ack]))
  return [...latest.values()].every((ack) => {
    const row = rows[ack.key]
    return (
      row !== undefined &&
      matchesSavedMetadata(
        row,
        ack.savedBaseline,
        ack.updatedAt,
        ack.carriedOverFromGroups,
      ) &&
      (view === 'actor' || baselineKey(row) === baselineKey(ack.savedBaseline))
    )
  })
}

/** Server projections are comparison-only; actor projections preserve working fields. */
export function projectOwnSaveAcknowledgements(
  rows: Readonly<Record<string, RetailerEditRow>>,
  acknowledgements: readonly DraftSaveAcknowledgement[],
  view: 'actor' | 'server' = 'actor',
): Record<string, RetailerEditRow> {
  const projected = { ...rows }
  for (const ack of acknowledgements) {
    const row = projected[ack.key]
    if (
      row === undefined ||
      !matchesSavedMetadata(
        row,
        ack.previousBaseline,
        ack.previousExpectedVersion,
        ack.previousCarriedOverFromGroups,
      ) ||
      (view === 'server' &&
        baselineKey(row) !== baselineKey(ack.previousBaseline))
    )
      continue
    projected[ack.key] = {
      ...row,
      ...(view === 'server' ? structuredClone(ack.savedBaseline) : {}),
      baseline: structuredClone(ack.savedBaseline),
      draftUpdatedAt: structuredClone(ack.updatedAt),
      carriedOverFromGroups: ack.carriedOverFromGroups,
    }
  }
  return projected
}

/** Content and baseline version, rather than polling object identity. */
export function aggregateFingerprint(
  rows: Readonly<Record<string, RetailerEditRow>>,
): string {
  return JSON.stringify(
    Object.entries(rows)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, row]) => [
        key,
        row.unit,
        row.retailerBusinessCd,
        row.forecastSlots.map((v) =>
          v === undefined ? null : numberToPlainString(v),
        ),
        row.providerSlots,
        [...row.manualEditKomas].sort((a, b) => a - b),
        row.provider,
        row.providerOther,
        row.providerLabel,
        row.carriedOverFromGroups,
        row.draftUpdatedAt?.seconds.toString(),
        row.draftUpdatedAt?.nanos,
        baselineKey(row.baseline),
      ]),
  )
}

export function sameAggregatePartition(
  identities: readonly AggregateEditIdentity[] | undefined,
  keys: readonly string[],
): boolean {
  if (identities === undefined || identities.length !== keys.length)
    return false
  const received = identities.map(aggregateIdentityKey)
  return (
    new Set(received).size === keys.length &&
    received.every((k) => k !== undefined && keys.includes(k))
  )
}

/** Validate the entire response before constructing any replacement row. */
export function adoptAggregateResult(
  rows: Record<string, RetailerEditRow>,
  response: PreviewAggregateEditResponse,
  gc: number,
): Record<string, RetailerEditRow> | undefined {
  const keys = Object.keys(rows)
  if (
    response.gcFrontier !== gc ||
    response.effectiveTotalSlots.length !== 48 ||
    !sameAggregatePartition(response.resolvedPartition?.identities, keys) ||
    !sameAggregatePartition(
      response.unitResults
        .map((r) => r.identity)
        .filter((i): i is AggregateEditIdentity => i !== undefined),
      keys,
    )
  )
    return undefined
  const next = { ...rows }
  for (const koma of new Set(
    response.unitResults.flatMap((r) => r.changedKomas),
  )) {
    if (koma <= gc) continue
    const total = response.effectiveTotalSlots[koma - 1]?.value?.value
    if (total === undefined || parseAggregateInteger(total) === undefined)
      return undefined
  }
  for (const result of response.unitResults) {
    const key = aggregateIdentityKey(result.identity)
    if (key === undefined || result.slots.length !== 48) return undefined
    const row = rows[key]
    if (row === undefined) return undefined
    const slots = [...row.forecastSlots]
    const manual = new Set(row.manualEditKomas)
    for (const koma of result.changedKomas) {
      if (!Number.isInteger(koma) || koma < 1 || koma > 48) return undefined
      if (koma <= gc) continue
      const raw = result.slots[koma - 1]?.value?.value
      const value = raw === undefined ? undefined : parseAggregateInteger(raw)
      if (value === undefined) return undefined
      slots[koma - 1] = value
      if (value !== row.forecastSlots[koma - 1]) manual.add(koma)
    }
    next[key] = { ...row, forecastSlots: slots, manualEditKomas: manual }
  }
  return next
}
